import { Button, Container, Grid2, Stack } from "@mui/material";
import { HeaderView } from "../../component/HeaderView";
import { LoadingView } from "../../component/LoadingView";
import { useTranslation } from "react-i18next";
import { ResetQuestionsViewModel } from "./ResetQuestionsViewModel";
import {
  provideGetAllCategoriesUseCase,
  providePlayerResetQuestionsUseCase,
} from "../../di/UseCasesModule";
import { Category } from "../../../domain/model/Category";
import { useRef, useState } from "react";
import useDoubleClick from "use-double-click";
import { CustomImageView } from "../../component/CustomImageView";
import { CustomTextView } from "../../component/CustomTextView";
import {
  COLOR_BLACK,
  COLOR_LIGHT_GRAY,
  COLOR_MAIN,
  COLOR_WHITE,
} from "../../../data/colors/Colors";
import { CustomCardView } from "../../component/CustomCardView";
import { CustomButtonView } from "../../component/CustomButtonView";
import useWindowDimensions from "../../../core/utils/UseWindowDimensions";

export function ResetQuestionsView() {
  const viewModel = ResetQuestionsViewModel(
    provideGetAllCategoriesUseCase(),
    providePlayerResetQuestionsUseCase()
  );
  const { t } = useTranslation();

  const { width } = useWindowDimensions();

  const hasQuestionsToReset =
    viewModel.getQuestionsToReset().subcategoriesToReset.length > 0;
  return (
    <Stack>
      <HeaderView
        title={t("reset_questions")}
        isLayoutFullWidthInSmallerScreens={false}
        layout={
          <Stack
            direction={"row"}
            alignItems={"center"}
            width={"100%"}
            justifyContent={"end"}
          >
            <CustomButtonView
              text={t("reset")}
              props={{ paddingLeft: 2, paddingRight: 2 }}
              disabled={
                viewModel.categories.length == 0 || !hasQuestionsToReset
              }
              onClick={viewModel.resetQuestions}
            />
          </Stack>
        }
      />

      {viewModel.loading ? (
        <LoadingView />
      ) : (
        <Container maxWidth={false}>
          <Grid2 container spacing={2} pb={3} pt={3}>
            <Grid2 size={12}>
              <CustomTextView
                text={t("reset_questions_screen_description")}
                textAlign="center"
                textColor={COLOR_LIGHT_GRAY}
                variant={width < 700 ? "subtitle2" : "subtitle1"}
              />
            </Grid2>
            {viewModel.categories?.map((category) => {
              return (
                <Grid2
                  size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 3 }}
                  key={category.id}
                >
                  <CategoryItemView
                    category={category}
                    onCategoryClicked={viewModel.onCategoryClicked}
                    onSubcategoryClicked={viewModel.onSubcategoryClicked}
                    onCategoryDoubleClicked={viewModel.selectAll}
                  />
                </Grid2>
              );
            })}
          </Grid2>
        </Container>
      )}
    </Stack>
  );
}

function CategoryItemView({
  category,
  onCategoryClicked,
  onCategoryDoubleClicked,
  onSubcategoryClicked,
}: {
  category: Category;
  onCategoryClicked: (categoryId: string) => void;
  onCategoryDoubleClicked: () => void;
  onSubcategoryClicked: (categoryId: string, subcategoryName: string) => void;
}) {
  const buttonRef = useRef(null);

  useDoubleClick({
    onSingleClick: (e) => {
      if (subcategoryClicked) return;
      onCategoryClicked(category.id);
    },
    onDoubleClick: (e) => {
      onCategoryDoubleClicked();
    },
    ref: buttonRef,
    latency: 250,
  });

  var subcategoryClicked = false;

  return (
    <Button
      ref={buttonRef}
      sx={{ p: 0, textTransform: "none", height: "100%", borderRadius: 6 }}
      fullWidth
    >
      <CustomCardView
        padding={0}
        width={"100%"}
        height={"100%"}
        children={
          <Stack
            spacing={0}
            height={"100%"}
            width={"100%"}
            alignItems={"center"}
          >
            <Stack
              sx={{
                height: "30vh",
                width: "100%",
              }}
            >
              <CustomImageView
                src={category.imageUrl}
                width={"100%"}
                height={"100%"}
                props={{
                  borderRadius: 6,
                  background: category.isSelected ? COLOR_MAIN : "lightGray",
                }}
              />
            </Stack>
            <Stack p={2} width={"100%"}>
              <Grid2 container spacing={1}>
                {category.subcategories.map((subcategory) => {
                  return (
                    <Grid2 key={subcategory.title}>
                      <Button
                        sx={{ p: 0, textTransform: "none", borderRadius: 6 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          onSubcategoryClicked(category.id, subcategory.title);
                          subcategoryClicked = true;

                          setTimeout(() => {
                            subcategoryClicked = false;
                          }, 300);
                        }}
                      >
                        <CustomCardView
                          padding={1}
                          background={
                            subcategory.isSelected ? COLOR_MAIN : COLOR_WHITE
                          }
                          children={
                            <CustomTextView
                              text={subcategory.title}
                              textColor={
                                subcategory.isSelected
                                  ? COLOR_WHITE
                                  : COLOR_BLACK
                              }
                              fontWeight={
                                subcategory.isSelected ? "bold" : "normal"
                              }
                            />
                          }
                        />
                      </Button>
                    </Grid2>
                  );
                })}
              </Grid2>
            </Stack>
          </Stack>
        }
      />
    </Button>
  );
}
